import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Mail } from 'lucide-react';

const LoginPage = () => {
  const { loginWithEmail, loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await loginWithEmail(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const code = error?.code;
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        toast.error('Invalid email or password.');
      } else if (code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please try again later.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        toast.error('Google sign-in failed. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-green pt-20 flex items-center justify-center">
      <div className="w-full max-w-md px-6 py-12">
        {/* Logo */}
        <div className="text-center mb-10">
          <img 
            src="/logo.png" 
            alt="Fairway Alliance"
            className="h-20 w-auto mx-auto mb-4"
          />
          <h1 className="font-display font-bold text-3xl text-white mb-2">Welcome Back</h1>
          <p className="text-cream/70 font-body">Sign in to your Fairway Alliance account</p>
        </div>

        {/* Google Sign In */}
        <Button
          type="button"
          variant="outline"
          className="w-full py-6 mb-6 bg-white hover:bg-gray-50 text-gray-800 border-white/20 font-semibold"
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          data-testid="login-google"
        >
          {googleLoading ? (
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          ) : (
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          Continue with Google
        </Button>

        {/* Apple Sign In */}
        <Button
          type="button"
          variant="outline"
          className="w-full py-6 mb-6 bg-black hover:bg-gray-900 text-white border-white/20 font-semibold"
          onClick={async () => {
            setAppleLoading(true);
            try {
              await loginWithApple();
              toast.success('Welcome back!');
              navigate('/dashboard');
            } catch (error) {
              if (error?.code !== 'auth/popup-closed-by-user') {
                toast.error('Apple sign-in failed. Please try again.');
              }
            } finally { setAppleLoading(false); }
          }}
          disabled={appleLoading}
          data-testid="login-apple"
        >
          {appleLoading ? (
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          ) : (
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          )}
          Continue with Apple
        </Button>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/20" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-deep-green text-cream/50">or sign in with email</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-cream/90">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-gold"
              data-testid="login-email"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-cream/90">Password</Label>
              <button
                type="button"
                onClick={() => {
                  if (!email) {
                    toast.error('Please enter your email first');
                    return;
                  }
                  import('../firebase').then(({ sendPasswordResetEmail, auth: fbAuth }) => {
                    sendPasswordResetEmail(fbAuth, email)
                      .then(() => toast.success('Password reset email sent! Check your inbox.'))
                      .catch(() => toast.error('Failed to send reset email. Please check your email address.'));
                  });
                }}
                className="text-gold text-sm hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-gold pr-10"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full btn-gold py-6"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing In...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Sign In with Email
              </>
            )}
          </Button>
        </form>

        {/* Register Link */}
        <p className="text-center mt-8 text-cream/70 font-body">
          Don't have an account?{' '}
          <Link to="/register" className="text-gold hover:underline font-semibold">
            Join Now
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
